import { IDate } from '@models';
import { NotificationService, Order } from '@services';
import { useStore } from '@stores';
import { formatDateWithOffset, roundUpToNext15, Rules } from '@utils';
import { Button, Checkbox, Col, Form, Input, message, Modal, Row, Typography } from 'antd';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { DateTimeSelect } from './DateTimeSelect';

const { Text } = Typography;

interface IProps {
  open: boolean;
  order: Order;
  title?: string;
  isInitial?: boolean;
  setOpen: (open: boolean) => void;
  onCancel: () => void;
}

export const DropOffDateModalForm: React.FC<IProps> = observer(
  ({ open, order, title, isInitial = false, setOpen, onCancel }) => {
    const { orderStore } = useStore();
    const [form] = Form.useForm();

    const [dropOff, setDropOff] = useState<IDate>();

    useEffect(() => {
      if (!order) return;

      const now = dayjs();
      const dropOffMoment = dayjs(order.dropOffDate);

      const effectiveNow = roundUpToNext15(now);

      const startAt = dropOffMoment.isBefore(now)
        ? formatDateWithOffset(effectiveNow)
        : order.dropOffDate;

      setDropOff({ startAt });

      form.setFieldsValue({
        id: order.id,
        sendPush: true,
        userId: order.userId,
        startAt: order.startAt ? formatDateWithOffset(order.startAt) : undefined,
        endAt: order.endAt ? formatDateWithOffset(order.endAt) : undefined,
        dropOffDate: startAt,
      });
    }, [form, order]);

    const handleUpdate = async (values: any) => {
      if (dayjs(values.dropOffDate).isBefore(dayjs().format())) {
        message.error('The selected date and time cannot be in the past.');
        return;
      }
      setOpen(false);
      await NotificationService.create({
        isInitial,
        sendPush: values.sendPush,
        orderId: values.id,
        dropOffDate: values.dropOffDate,
      }).then(async () => {
        await orderStore
          .updateCalendar({
            id: values.id,
            userId: values.userId,
            startAt: values.startAt,
            endAt: values.endAt,
            workStationId: order.workStationId,
            dropOffDate: values.dropOffDate,
          })
          .then(() => form.resetFields());
      });
    };

    const setDropOffDate = (value: IDate) => {
      setDropOff({ startAt: formatDateWithOffset(value.startAt) });
      form.setFieldsValue({ dropOffDate: formatDateWithOffset(value.startAt) });
    };

    return (
      <Modal
        title={
          <Row>
            <Col>
              <Text>{title || 'Customer drop off time confirmation'}</Text>
            </Col>
          </Row>
        }
        transitionName=""
        maskTransitionName=""
        open={open}
        onCancel={onCancel}
        afterClose={() => form.resetFields()}
        footer={null}
      >
        <Form form={form} style={{ paddingTop: 30 }} onFinish={handleUpdate}>
          <Form.Item name="id" hidden>
            <Input size="small" />
          </Form.Item>
          <Form.Item name="startAt" hidden>
            <Input size="small" />
          </Form.Item>
          <Form.Item name="endAt" hidden>
            <Input size="small" />
          </Form.Item>
          <Form.Item name="userId" hidden>
            <Input size="small" />
          </Form.Item>
          <Row gutter={8}>
            <Col>
              <Form.Item
                required
                name="dropOffDate"
                rules={Rules.required}
                label="Drop off date & time"
              >
                <DateTimeSelect dateTime={dropOff} setDate={setDropOffDate} />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col>
              <Form.Item name="sendPush" valuePropName="checked">
                <Checkbox>Send mobile App notification</Checkbox>
              </Form.Item>
            </Col>
          </Row>
          <Row justify="end">
            <Col>
              <Row gutter={4}>
                <Col>
                  <Form.Item style={{ margin: 0, padding: 0 }}>
                    <Button
                      loading={orderStore.calendarLoading}
                      disabled={orderStore.calendarLoading}
                      onClick={onCancel}
                    >
                      Cancel
                    </Button>
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item style={{ margin: 0, padding: 0 }}>
                    <Button
                      loading={orderStore.calendarLoading}
                      disabled={orderStore.calendarLoading}
                      type="primary"
                      htmlType="submit"
                    >
                      Save
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      </Modal>
    );
  }
);
